import * as React from 'react';

export interface ReactAutoExpandTextAreaData {
    id?: string,
    text: string,
    style?: React.CSSProperties,
    css?: string,
    row?: any,
    rowIndex?: number,
    onChange?: (rowOBJ: any, newText: string, rowIndex: number) => void,
    onBlur?: (rowOBJ: any, text: string, rowIndex: number) => void,
    onFocus?: (rowOBJ: any, rowIndex: number) => void,
    disabled?: boolean,
    disableID?: string,
    tabIndex?: null,
    title?: string,
    placeholder?: string,
    dataKey?: string,
    maxLength?: number,
    priority?: 'immediate' | 'high' | 'normal' | 'low',
}

export interface ReactAutoExpandTextAreaState {
    disabled: boolean,
    text: string,
    row: any,
    userIsTyping: boolean,
}

export class ReactAutoExpandTextArea extends React.Component<ReactAutoExpandTextAreaData, ReactAutoExpandTextAreaState>{
    textAreaRef: React.RefObject<HTMLTextAreaElement>;

    // Computed dimensions — written directly to the DOM element in adjustSize(),
    // and read by render() via getComputedDimensions(). No setState needed for sizing:
    // this breaks the "batched setState overwrites direct DOM style" race.
    private computedHeight: number = 0;
    private computedWidth: number = 0;

    // Sizing cache — avoids redundant DOM measurement when text unchanged
    private lastCalculatedText: string = '';

    // Native listeners for external value changes (jQuery, etc.)
    private nativeInputListener?: EventListener;
    private nativeChangeListener?: EventListener;

    // Cached font string — avoids getComputedStyle on every resize
    private cachedFont: string = '';
    // Cached canvas context for text width measurement (no DOM mutation)
    private measureCtx: CanvasRenderingContext2D | null = null;

    constructor(props: ReactAutoExpandTextAreaData) {
        super(props);

        this.state = {
            disabled: props.disabled,
            text: props.text,
            row: props.row,
            userIsTyping: false,
        };

        this.textAreaRef = React.createRef();

        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.onDisableClick = this.onDisableClick.bind(this);
        this.adjustSize = this.adjustSize.bind(this);
        this.getNumericStyleValue = this.getNumericStyleValue.bind(this);
        this.syncExternalValueAndResize = this.syncExternalValueAndResize.bind(this);
        this.applyTextValue = this.applyTextValue.bind(this);
        this.onAdjustSizeClick = this.onAdjustSizeClick.bind(this);
    }

    shouldComponentUpdate(nextProps: ReactAutoExpandTextAreaData, nextState: ReactAutoExpandTextAreaState) {
        return (
            nextProps.text !== this.props.text ||
            nextProps.disabled !== this.props.disabled ||
            nextProps.style !== this.props.style ||
            nextProps.row !== this.props.row ||
            nextState.text !== this.state.text ||
            nextState.disabled !== this.state.disabled
        );
    }

    getNumericStyleValue(value: any, defaultValue: number): number {
        if (value === undefined || value === null) return defaultValue;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parsed = parseInt(value.replace('px', ''));
            return isNaN(parsed) ? defaultValue : parsed;
        }
        return defaultValue;
    }

    // Returns the current computed dimensions for use in render().
    // Falls back to style min values if not yet measured.
    private getComputedDimensions(): { height: number; width: number } {
        if (this.computedHeight > 0 && this.computedWidth > 0) {
            return { height: this.computedHeight, width: this.computedWidth };
        }
        return {
            height: this.getNumericStyleValue(this.props.style?.minHeight, 20),
            width: this.getNumericStyleValue(this.props.style?.minWidth, 100),
        };
    }

    componentDidMount() {
        const textArea = this.textAreaRef.current;
        if (textArea) {
            this.nativeInputListener = () => this.syncExternalValueAndResize(true);
            this.nativeChangeListener = () => this.syncExternalValueAndResize(true);
            textArea.addEventListener('input', this.nativeInputListener);
            textArea.addEventListener('change', this.nativeChangeListener);

            // Cache font string once — avoids getComputedStyle on every resize
            try {
                this.cachedFont = window.getComputedStyle(textArea).font || '11px Verdana';
            } catch (_) {
                this.cachedFont = '11px Verdana';
            }

            // Create a canvas context for measuring text width without DOM mutation.
            try {
                this.measureCtx = document.createElement('canvas').getContext('2d');
                if (this.measureCtx) {
                    this.measureCtx.font = this.cachedFont;
                }
            } catch (_) {
                this.measureCtx = null;
            }
        }

        // Measure and apply size synchronously — DOM is committed at this point.
        // adjustSize() writes directly to textArea.style so the textarea is visible
        // at the correct size immediately, before any React setState flush.
        this.adjustSize();
    }

    componentWillUnmount() {
        const textArea = this.textAreaRef.current;
        if (textArea && this.nativeInputListener) {
            textArea.removeEventListener('input', this.nativeInputListener);
        }
        if (textArea && this.nativeChangeListener) {
            textArea.removeEventListener('change', this.nativeChangeListener);
        }
    }

    private applyTextValue(newText: string, userIsTyping: boolean, fireOnChange: boolean = false) {
        const currentText = this.state.text || '';
        if (newText === currentText) {
            this.adjustSize();
            return;
        }

        this.setState((prevState) => {
            const updatedRow = prevState.row;
            if (this.props.dataKey && updatedRow) {
                updatedRow[this.props.dataKey] = newText;
            }
            return { text: newText, row: updatedRow, userIsTyping };
        }, () => {
            if (fireOnChange && null != this.props.onChange && !this.state.disabled) {
                this.props.onChange(this.state.row, newText, this.props.rowIndex);
            }
            this.adjustSize();
        });
    }

    private syncExternalValueAndResize(markUserTyping: boolean = false) {
        const textArea = this.textAreaRef.current;
        if (!textArea) return;

        const domValue = textArea.value || '';
        const stateValue = this.state.text || '';

        if (domValue !== stateValue) {
            this.applyTextValue(domValue, markUserTyping, true);
            return;
        }

        this.adjustSize();
    }

    componentDidUpdate(prevProps: ReactAutoExpandTextAreaData, prevState: ReactAutoExpandTextAreaState) {
        if (prevProps.disabled !== this.props.disabled) {
            this.setState({ disabled: this.props.disabled });
        }

        if (prevProps.row !== this.props.row) {
            this.setState({ row: this.props.row });
        }

        if (prevProps.text !== this.props.text) {
            if (!this.state.userIsTyping) {
                this.setState({ text: this.props.text }, () => {
                    this.adjustSize();
                });
                return;
            }
        }

        // If external code changed the real textarea value, sync it back into state
        const textArea = this.textAreaRef.current;
        if (textArea && textArea.value !== (this.state.text || '')) {
            this.applyTextValue(textArea.value || '', this.state.userIsTyping, true);
            return;
        }

        if (prevState.text !== this.state.text) {
            this.adjustSize();
        }
    }

    adjustSize() {
        const textArea = this.textAreaRef.current;
        if (!textArea) return;

        const currentText = this.state.text || '';

        const minHeight = this.getNumericStyleValue(this.props.style?.minHeight, 25);
        const minWidth = this.getNumericStyleValue(this.props.style?.minWidth, 100);
        const maxHeight = this.getNumericStyleValue(this.props.style?.maxHeight, undefined);
        const maxWidth = this.getNumericStyleValue(this.props.style?.maxWidth, undefined);

        // Cache hit — text unchanged, dimensions already measured — just re-apply to DOM
        if (currentText === this.lastCalculatedText &&
            this.computedHeight >= minHeight &&
            this.computedWidth >= minWidth) {
            textArea.style.height = this.computedHeight + 'px';
            textArea.style.width = this.computedWidth + 'px';
            return;
        }

        // Measure width using canvas.measureText() ��� no DOM mutation, no reflow
        const textToMeasure = currentText || this.props.placeholder || '';
        let measuredWidth = 0;
        if (this.measureCtx) {
            const lines = textToMeasure.split('\n');
            for (let i = 0; i < lines.length; i++) {
                const lineWidth = this.measureCtx.measureText(lines[i]).width;
                if (lineWidth > measuredWidth) measuredWidth = lineWidth;
            }
            measuredWidth += 20; // padding
        } else {
            measuredWidth = textArea.scrollWidth;
        }

        let newWidth: number;
        if (maxWidth && measuredWidth >= maxWidth) {
            newWidth = maxWidth;
        } else if (measuredWidth < minWidth) {
            newWidth = minWidth;
        } else {
            newWidth = measuredWidth;
        }

        // Measure height — temporarily shrink to 1px to force scrollHeight recalculation
        textArea.style.width = newWidth + 'px';
        textArea.style.height = '1px';
        let newHeight = textArea.scrollHeight;

        if (maxHeight && newHeight > maxHeight) {
            newHeight = maxHeight;
        } else if (newHeight < minHeight) {
            newHeight = minHeight;
        }

        // Write dimensions directly to DOM — immediately visible, no setState batching delay
        textArea.style.height = newHeight + 'px';
        textArea.style.width = newWidth + 'px';

        // Store computed dimensions and cache key
        this.computedHeight = newHeight;
        this.computedWidth = newWidth;
        this.lastCalculatedText = currentText;
    }

    handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.applyTextValue(event.target.value, true, true);
    }

    handleBlur(event: React.FocusEvent<HTMLTextAreaElement>) {
        this.syncExternalValueAndResize(false);
        this.setState({ userIsTyping: false });
        if (null != this.props.onBlur && !this.state.disabled) {
            this.props.onBlur(this.props.row, this.state.text, this.props.rowIndex);
        }
    }

    handleFocus(event: React.FocusEvent<HTMLTextAreaElement>) {
        const currentText = this.state.text || '';
        const alreadySized = currentText === this.lastCalculatedText &&
            this.computedHeight > 0 &&
            this.computedWidth > 0;
        if (!alreadySized) {
            this.syncExternalValueAndResize(true);
        }
        if (null != this.props.onFocus && !this.state.disabled) {
            this.props.onFocus(this.props.row, this.props.rowIndex);
        }
    }

    onAdjustSizeClick() {
        this.syncExternalValueAndResize(false);
    }

    onDisableClick = (event: any) => {
        const isChecked = $(event.currentTarget).is(":checked");
        this.setState({ disabled: isChecked });
    }

    getTextArea() {
        const maxHeight = this.getNumericStyleValue(this.props.style?.maxHeight, undefined);
        const maxWidth = this.getNumericStyleValue(this.props.style?.maxWidth, undefined);
        const overflowY = maxHeight ? 'auto' : 'hidden';
        const overflowX = maxWidth ? 'auto' : 'hidden';

        // Use computedHeight/computedWidth directly — these are always up-to-date
        // because adjustSize() writes them synchronously in componentDidMount and
        // whenever text changes. Never rely on setState for dimensions to avoid
        // the "batched setState overwrites direct DOM style" race during expand-all.
        const { height, width } = this.getComputedDimensions();

        const style: React.CSSProperties = {
            ...this.props.style,
            height: height + 'px',
            width: width + 'px',
            resize: 'none',
            overflowY: overflowY,
            overflowX: overflowX,
            boxSizing: 'border-box',
        };

        return (
            <textarea
                ref={this.textAreaRef}
                id={this.props.id}
                tabIndex={this.props.tabIndex}
                className={this.props.css}
                style={style}
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                onFocus={this.handleFocus}
                value={this.state.text}
                disabled={this.state.disabled}
                title={this.props.title}
                placeholder={this.props.placeholder}
                maxLength={this.props.maxLength}
            />
        );
    }

    render() {
        return <React.Fragment>
            {<table className="categoryAllHide"><tbody><tr>
                <td><input
                    id={this.props.disableID}
                    type="checkbox"
                    onChange={this.onDisableClick}
                    checked={this.state.disabled}
                /></td>
                <td>
                    HIDDEN DISABLE BUTTON
                </td>
                <td><button
                    type="button"
                    id={this.props.id ? this.props.id + '_adjustSize' : undefined}
                    onClick={this.onAdjustSizeClick}
                >HIDDEN ADJUST SIZE BUTTON</button></td>
            </tr></tbody></table>
            }
            {this.getTextArea()}
        </React.Fragment>;
    }
}
